// Google Analytics 4 tracking utilities

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    pintrk: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
    rdt: (...args: any[]) => void;
  }
}

// Initialize GA4 if not already loaded
export const initializeGA = (measurementId: string) => {
  if (typeof window === 'undefined') return;
  
  // Add GA4 script if not already present
  if (!document.querySelector(`script[src*="${measurementId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    page_title: document.title,
    page_location: window.location.href,
  });
};

// Generic event tracking function
export const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  console.log('🔍 GA4 Event:', eventName, parameters);
  window.gtag('event', eventName, parameters);
};

// Page view tracking
export const trackPageView = (pageName: string, additionalParams: Record<string, any> = {}) => {
  trackEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href,
    page_path: window.location.pathname,
    ...additionalParams
  });
};

// 1. Landing page visit
export const trackLandingPageView = () => {
  trackPageView('Landing Page', {
    page_category: 'marketing',
    content_group1: 'home'
  });
};

// 2. Address search initiated
export const trackAddressSearch = (address: string, searchMethod: 'manual' | 'autocomplete' = 'manual') => {
  trackEvent('address_search', {
    address_length: address.length,
    search_method: searchMethod,
    content_group1: 'search'
  });
};

// 3. Image processing started
export const trackImageProcessingStarted = (address: string, imageSource: 'address' | 'upload' | 'webcam') => {
  trackEvent('image_processing_started', {
    image_source: imageSource,
    has_address: !!address,
    content_group1: 'processing'
  });
};

// 4. Configurator reached
export const trackConfiguratorView = (hasGeneratedImages: boolean, imageSource?: string) => {
  trackPageView('Configurator', {
    page_category: 'product',
    content_group1: 'configure'
  });
  
  trackEvent('configurator_reached', {
    has_generated_images: hasGeneratedImages,
    image_source: imageSource || 'unknown',
    content_group1: 'configure'
  });
};

// 5. Product configuration events
export const trackStyleSelection = (style: string) => {
  trackEvent('style_selected', {
    style_type: style,
    content_group1: 'configure'
  });
};

export const trackFrameSelection = (frameType: string) => {
  trackEvent('frame_selected', {
    frame_type: frameType,
    content_group1: 'configure'
  });
};

export const trackSizeSelection = (size: string) => {
  trackEvent('size_selected', {
    size_type: size,
    content_group1: 'configure'
  });
};

// 6. Order button clicked
export const trackOrderButtonClick = (productConfig: any) => {
  trackEvent('order_button_clicked', {
    style: productConfig.style,
    frame: productConfig.frame,
    size: productConfig.size,
    value: productConfig.price || 0,
    currency: 'USD',
    content_group1: 'purchase_intent'
  });
};

// 7. Order summary reached
export const trackOrderSummaryView = (orderDetails: any) => {
  trackPageView('Order Summary', {
    page_category: 'checkout',
    content_group1: 'order_summary'
  });
  
  // Enhanced ecommerce: Begin checkout
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: orderDetails.total || 0,
    items: [{
      item_id: orderDetails.productId || 'frame',
      item_name: `${orderDetails.style || 'watercolor'} Frame`,
      item_category: 'home_decor',
      item_variant: `${orderDetails.frame || 'black'}-${orderDetails.size || 'medium'}`,
      price: orderDetails.price || 0,
      quantity: 1
    }]
  });
};

// 8. Checkout button clicked
export const trackCheckoutButtonClick = (orderDetails: any) => {
  trackEvent('checkout_button_clicked', {
    order_value: orderDetails.total || 0,
    currency: 'USD',
    payment_method: 'stripe',
    content_group1: 'checkout'
  });
};

// 9. Stripe checkout reached
export const trackStripeCheckoutView = (sessionId?: string) => {
  trackPageView('Stripe Checkout', {
    page_category: 'payment',
    content_group1: 'payment'
  });
  
  trackEvent('stripe_checkout_reached', {
    session_id: sessionId || 'unknown',
    content_group1: 'payment'
  });
};

// 10. Purchase completed
export const trackPurchaseCompleted = (orderDetails: any) => {
  // Enhanced ecommerce: Purchase
  trackEvent('purchase', {
    transaction_id: orderDetails.orderId || orderDetails.transactionId,
    value: orderDetails.total || 0,
    currency: 'USD',
    tax: orderDetails.tax || 0,
    shipping: orderDetails.shipping || 0,
    items: [{
      item_id: orderDetails.productId || 'frame',
      item_name: `${orderDetails.style || 'watercolor'} Frame`,
      item_category: 'home_decor',
      item_variant: `${orderDetails.frame || 'black'}-${orderDetails.size || 'medium'}`,
      price: orderDetails.price || 0,
      quantity: 1
    }]
  });
};

// 11. Order confirmation reached
export const trackOrderConfirmationView = (orderDetails: any) => {
  trackPageView('Order Confirmation', {
    page_category: 'confirmation',
    content_group1: 'order_confirmation'
  });
  
  trackEvent('order_confirmation_viewed', {
    order_id: orderDetails.orderId,
    order_value: orderDetails.total || 0,
    currency: 'USD',
    content_group1: 'order_confirmation'
  });
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string, context?: string) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit message length
    error_context: context || 'unknown',
    content_group1: 'error'
  });
};

// Image generation tracking
export const trackImageGeneration = (source: 'address' | 'upload' | 'webcam', success: boolean, duration?: number) => {
  trackEvent('image_generation', {
    generation_source: source,
    generation_success: success,
    generation_duration: duration,
    content_group1: 'image_processing'
  });
};

// User engagement tracking
export const trackUserEngagement = (action: string, details?: Record<string, any>) => {
  trackEvent('user_engagement', {
    engagement_action: action,
    ...details,
    content_group1: 'engagement'
  });
};

// Conversion funnel steps (for easy funnel analysis)
export const trackFunnelStep = (stepName: string, stepNumber: number, additionalData?: Record<string, any>) => {
  trackEvent('funnel_step', {
    step_name: stepName,
    step_number: stepNumber,
    ...additionalData
  });
}; 

// Set user properties for better segmentation
export const setUserProperties = (properties: Record<string, string>) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'GA_MEASUREMENT_ID';
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_properties: properties
  });
};

// Track user type for segmentation
export const identifyUser = (userType: 'new' | 'returning', imageSource?: string) => {
  setUserProperties({
    user_type: userType,
    primary_image_source: imageSource || 'unknown',
    acquisition_date: new Date().toISOString().split('T')[0] // YYYY-MM-DD
  });
}; 

// Individual User ID tracking
export const getUserIdFromUrl = (): string | null => {
  // Get user ID from URL path (e.g., framemyhome.ai/AHSBDD)
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const userId = pathSegments[0]; // First segment after domain
  
  // Validate user ID (alphanumeric, 4-12 characters)
  if (userId && /^[A-Za-z0-9]{4,12}$/.test(userId)) {
    return userId.toUpperCase();
  }
  
  // Also check URL parameters as fallback
  const urlParams = new URLSearchParams(window.location.search);
  const paramUserId = urlParams.get('user') || urlParams.get('id') || urlParams.get('ref');
  
  if (paramUserId && /^[A-Za-z0-9]{4,12}$/.test(paramUserId)) {
    return paramUserId.toUpperCase();
  }
  
  return null;
};

// Store user ID in session storage for persistence
export const storeUserId = (userId: string) => {
  sessionStorage.setItem('tracked_user_id', userId);
  localStorage.setItem('tracked_user_id', userId); // Also store in localStorage for longer persistence
  console.log('🎯 User ID stored for tracking:', userId);
};

// Get stored user ID
export const getStoredUserId = (): string | null => {
  return sessionStorage.getItem('tracked_user_id') || localStorage.getItem('tracked_user_id');
};

// Set user ID tracking in GA4
export const setUserIdTracking = (userId: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'GA_MEASUREMENT_ID';
  
  // Set user ID in GA4 for cross-session tracking
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
    user_properties: {
      tracked_user_id: userId,
      tracking_source: 'direct_link',
      user_type: 'tracked_individual'
    }
  });
  
  // Track initial user entry
  trackEvent('user_entry', {
    tracked_user_id: userId,
    entry_url: window.location.href,
    tracking_source: 'direct_link',
    session_start: true
  });
};

// Enhanced tracking with user ID using built-in GA4 parameters
export const trackEventWithUserId = (eventName: string, parameters: Record<string, any> = {}) => {
  const userId = getStoredUserId();
  
  const enhancedParams = {
    ...parameters,
    ...(userId && { 
      // Use built-in GA4 parameters that don't require custom dimensions
      user_id: userId, // Built-in GA4 parameter
      custom_parameter_1: userId, // Alternative parameter
      user_tracking: true,
      tracked_user_id: userId // Keep our custom parameter too
    })
  };
  
  trackEvent(eventName, enhancedParams);
};

// Enhanced funnel step tracking with user ID
export const trackFunnelStepWithUserId = (stepName: string, stepNumber: number, additionalData?: Record<string, any>) => {
  const userId = getStoredUserId();
  
  trackFunnelStep(stepName, stepNumber, {
    ...additionalData,
    ...(userId && { 
      tracked_user_id: userId,
      user_tracking: true 
    })
  });
};

// Track user milestone reached
export const trackUserMilestone = (milestone: string, additionalData?: Record<string, any>) => {
  const userId = getStoredUserId();
  
  if (userId) {
    trackEvent('user_milestone', {
      milestone_name: milestone,
      tracked_user_id: userId,
      timestamp: new Date().toISOString(),
      ...additionalData
    });
  }
};

// Generate user journey summary
export const trackUserJourneySummary = (journeyData: {
  startTime: string;
  currentStep: string;
  stepsCompleted: string[];
  totalEvents: number;
}) => {
  const userId = getStoredUserId();
  
  if (userId) {
    trackEvent('user_journey_summary', {
      tracked_user_id: userId,
      journey_start: journeyData.startTime,
      current_step: journeyData.currentStep,
      steps_completed: journeyData.stepsCompleted.join(','),
      total_events: journeyData.totalEvents,
      journey_duration: Date.now() - new Date(journeyData.startTime).getTime()
    });
  }
}; 

// Track user events with user ID in event name (no custom dimensions needed)
export const trackUserEventByName = (baseEventName: string, parameters: Record<string, any> = {}) => {
  const userId = getStoredUserId();
  
  if (userId) {
    // Create event name with user ID
    const eventName = `${baseEventName}_user_${userId}`;
    trackEvent(eventName, {
      base_event: baseEventName,
      user_reference: userId,
      ...parameters
    });
  } else {
    // Fallback to regular tracking
    trackEvent(baseEventName, parameters);
  }
}; 

// Development helper for testing user tracking locally
export const testUserTracking = (testUserId: string = 'TEST123') => {
  if (import.meta.env.DEV) {
    console.log('🧪 Testing user tracking with ID:', testUserId);
    
    // Simulate user journey
    storeUserId(testUserId);
    setUserIdTracking(testUserId);
    
    // Test milestone tracking
    trackUserMilestone('local_test_started', {
      test_environment: 'local_development',
      timestamp: new Date().toISOString()
    });
    
    console.log('✅ Test events sent. Check GA4 Realtime or DebugView.');
    return testUserId;
  } else {
    console.warn('⚠️ testUserTracking only works in development mode');
    return null;
  }
}; 

// Specific milestone events for different user journey paths
export const trackCustomLandingPageAccess = (userId: string, routeType: string) => {
  trackUserMilestone('landing_page_access_h_route', {
    route_type: routeType,
    user_id: userId,
    access_method: 'direct_link_h_suffix'
  });
};

export const trackCustomLandingPageLoaded = (propertyId: string, address: string, imageSource: string) => {
  trackUserMilestone('custom_landing_page_loaded', {
    property_id: propertyId,
    address: address,
    all_styles_available: true,
    image_source: imageSource,
    page_type: 'custom_landing_with_artwork'
  });
};

export const trackPartialImagesRedirect = (availableStyles: string[], redirectReason: string) => {
  trackUserMilestone('partial_images_redirect_to_configurator', {
    available_styles: availableStyles.join(','),
    redirect_reason: redirectReason,
    styles_count: availableStyles.length
  });
};

export const trackUserDataNotFound = (attemptedUserId: string, fallbackAction: string) => {
  trackEventWithUserId('user_data_not_found', {
    attempted_user_id: attemptedUserId,
    fallback_action: fallbackAction,
    search_method: 'property_token_lookup'
  });
};

// =========================
// Pinterest Pixel Tracking
// =========================

export const trackPinterestEvent = (eventType: string, parameters: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.pintrk) {
    console.warn('Pinterest pixel not available');
    return;
  }
  
  console.log('📌 Pinterest Event:', eventType, parameters);
  window.pintrk('track', eventType, parameters);
};

export const trackPinterestConfiguratorLaunch = (userEmail?: string, additionalData?: Record<string, any>) => {
  const eventData = {
    ...additionalData,
    event_source_type: 'web',
    user_data: userEmail ? { em: userEmail } : undefined
  };
  
  trackPinterestEvent('Custom', {
    ...eventData,
    custom_event_name: 'configurator_launch'
  });
  
  // Also track as a view content event for better Pinterest optimization
  trackPinterestEvent('ViewContent', eventData);
};

export const trackPinterestOrderStart = (orderValue?: number, userEmail?: string) => {
  trackPinterestEvent('InitiateCheckout', {
    value: orderValue || 0,
    currency: 'USD',
    event_source_type: 'web',
    user_data: userEmail ? { em: userEmail } : undefined
  });
};

export const trackPinterestPurchase = (orderDetails: any, userEmail?: string) => {
  trackPinterestEvent('Checkout', {
    value: orderDetails.total || 0,
    currency: 'USD',
    order_id: orderDetails.orderId,
    event_source_type: 'web',
    user_data: userEmail ? { em: userEmail } : undefined
  });
};

// =========================
// Facebook Pixel Tracking
// =========================

export const trackFacebookEvent = (eventType: string, parameters: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.fbq) {
    console.warn('Facebook pixel not available');
    return;
  }
  
  console.log('📘 Facebook Event:', eventType, parameters);
  window.fbq('track', eventType, parameters);
};

export const trackFacebookConfiguratorLaunch = (userEmail?: string, additionalData?: Record<string, any>) => {
  const eventData = {
    ...additionalData,
    content_category: 'home_decor',
    content_name: 'configurator_access'
  };
  
  // Add email if available
  if (userEmail) {
    eventData.user_data = { em: userEmail };
  }
  
  trackFacebookEvent('ViewContent', eventData);
  
  // Custom event for configurator launch
  trackFacebookEvent('CustomConfiguratorLaunch', eventData);
};

export const trackFacebookOrderStart = (orderValue?: number, userEmail?: string) => {
  const eventData: any = {
    value: orderValue || 0,
    currency: 'USD',
    content_category: 'home_decor'
  };
  
  if (userEmail) {
    eventData.user_data = { em: userEmail };
  }
  
  trackFacebookEvent('InitiateCheckout', eventData);
};

export const trackFacebookPurchase = (orderDetails: any, userEmail?: string) => {
  const eventData: any = {
    value: orderDetails.total || 0,
    currency: 'USD',
    content_ids: [orderDetails.productId || 'frame'],
    content_type: 'product',
    content_category: 'home_decor'
  };
  
  if (userEmail) {
    eventData.user_data = { em: userEmail };
  }
  
  trackFacebookEvent('Purchase', eventData);
};

// =========================
// Reddit Pixel Tracking
// =========================

export const trackRedditEvent = (eventType: string, parameters: Record<string, any> = {}) => {
  if (typeof window === 'undefined' || !window.rdt) {
    console.warn('Reddit pixel not available');
    return;
  }
  
  console.log('🤖 Reddit Event:', eventType, parameters);
  window.rdt('track', eventType, parameters);
};

export const trackRedditConfiguratorLaunch = (userEmail?: string, additionalData?: Record<string, any>) => {
  const eventData: any = {
    value: 0,
    currency: 'USD',
    customEventName: 'ConfiguratorLaunch'
  };
  
  // Add email if available (Reddit supports hashed emails)
  if (userEmail) {
    eventData.email = userEmail;
  }
  
  trackRedditEvent('ViewContent', eventData);
};

export const trackRedditOrderStart = (orderValue?: number, userEmail?: string) => {
  const eventData: any = {
    value: orderValue || 0,
    currency: 'USD',
    customEventName: 'OrderStart'
  };
  
  if (userEmail) {
    eventData.email = userEmail;
  }
  
  trackRedditEvent('AddToCart', eventData);
};

export const trackRedditPurchase = (orderDetails: any, userEmail?: string) => {
  const eventData: any = {
    value: orderDetails.total || 0,
    currency: 'USD',
    customEventName: 'OrderComplete'
  };
  
  if (userEmail) {
    eventData.email = userEmail;
  }
  
  trackRedditEvent('Purchase', eventData);
};

// =========================
// Combined Pixel Tracking
// =========================

export const trackConfiguratorLaunchAllPixels = (userEmail?: string, additionalData?: Record<string, any>) => {
  console.log('🎯 Tracking configurator launch across all pixels', { userEmail: !!userEmail, additionalData });
  
  // Track in Google Analytics (existing)
  trackConfiguratorView(true, additionalData?.imageSource);
  
  // Track in Pinterest
  trackPinterestConfiguratorLaunch(userEmail, additionalData);
  
  // Track in Facebook (when available)
  trackFacebookConfiguratorLaunch(userEmail, additionalData);
  
  // Track in Reddit
  trackRedditConfiguratorLaunch(userEmail, additionalData);
  
  // Track in LogRocket
  trackLogRocketConfiguratorLaunch({
    user_email: userEmail,
    has_email: !!userEmail,
    ...additionalData
  });
  
  // Track milestone for user ID tracking if available
  const userId = getStoredUserId();
  if (userId) {
      trackUserMilestone('configurator_launch_with_pixels', {
        has_email: !!userEmail,
        pixels_fired: ['google_analytics', 'pinterest', 'facebook', 'reddit', 'logrocket'].join(','),
        ...additionalData
      });
  }
}; 

// LogRocket has been removed - now using widget-based session recording